import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowRightLeft, Gift, Swords, Users, Check, X, Plus } from "lucide-react";
import { useLocale } from "@/hooks/useLocale";
import { toast } from "sonner";
import { InteractionCreateForm } from "./InteractionCreateForm";
import { InteractionCard } from "./InteractionCard";

interface Interaction {
  id: string;
  adventure_id: string;
  from_user_id: string;
  to_user_id: string;
  type: string;
  payload: any;
  status: string;
  created_at: string;
  resolved_at: string | null;
}

interface Props {
  adventureId: string;
  currentUserId: string | null;
  players: any[];
  onUnreadChange?: (count: number) => void;
}

export function PlayerInteractionsPanel({ adventureId, currentUserId, players, onUnreadChange }: Props) {
  const { t } = useLocale();
  const [interactions, setInteractions] = useState<Interaction[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const channelRef = useRef<any>(null);

  useEffect(() => {
    loadInteractions();
    const channel = supabase
      .channel(`interactions-${adventureId}`)
      .on("postgres_changes", {
        event: "*",
        schema: "public",
        table: "adventure_interactions",
        filter: `adventure_id=eq.${adventureId}`,
      }, () => {
        loadInteractions();
      })
      .subscribe();
    channelRef.current = channel;
    return () => { if (channelRef.current) supabase.removeChannel(channelRef.current); };
  }, [adventureId]);

  useEffect(() => {
    const pending = interactions.filter(
      (i) => i.status === "pending" && i.to_user_id === currentUserId
    ).length;
    onUnreadChange?.(pending);
  }, [interactions, currentUserId, onUnreadChange]);

  const loadInteractions = async () => {
    const { data } = await supabase
      .from("adventure_interactions")
      .select("*")
      .eq("adventure_id", adventureId)
      .order("created_at", { ascending: false })
      .limit(100) as any;
    if (data) setInteractions(data);
  };

  const handleRespond = async (interaction: Interaction, newStatus: "accepted" | "declined") => {
    await supabase.from("adventure_interactions")
      .update({ status: newStatus, resolved_at: new Date().toISOString() } as any)
      .eq("id", interaction.id);
    loadInteractions();
  };

  const handleCancel = async (interaction: Interaction) => {
    await supabase.from("adventure_interactions")
      .update({ status: "cancelled", resolved_at: new Date().toISOString() } as any)
      .eq("id", interaction.id);
    loadInteractions();
  };

  const getPlayerName = (userId: string) => {
    const p = players.find((pl) => pl.user_id === userId);
    return p?.character_name || p?.profile?.full_name || t.play_anonymous;
  };

  if (showCreate) {
    return (
      <InteractionCreateForm
        adventureId={adventureId}
        currentUserId={currentUserId}
        players={players}
        onClose={() => setShowCreate(false)}
        onCreated={() => { setShowCreate(false); loadInteractions(); }}
      />
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border shrink-0">
        <span className="text-xs font-semibold">{t.mp_interactions_title}</span>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => setShowCreate(true)} aria-label="New interaction">
          <Plus className="w-3 h-3" />
        </Button>
      </div>
      <ScrollArea className="flex-1 px-2 py-1">
        {interactions.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-6">{t.mp_no_interactions}</p>
        ) : (
          <div className="space-y-1.5">
            {interactions.map((ix) => (
              <InteractionCard
                key={ix.id}
                interaction={ix}
                currentUserId={currentUserId}
                getPlayerName={getPlayerName}
                onRespond={handleRespond}
                onCancel={handleCancel}
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
